import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  ClipboardCheck, Users, Calendar, Send, Save, Eye, FileText, 
  CheckCircle, Clock, AlertCircle, ChevronRight, Upload, Download,
  Star, MessageSquare, RefreshCw, Plus, Trash2, Maximize2, Minimize2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Final-score rating bands shown to employees after HR completes the appraisal
const getScoreRating = (score) => {
  if (score >= 90) return { label: 'Exceeding Expectation', textClass: 'text-emerald-600' };
  if (score >= 80) return { label: 'Meeting Expectation', textClass: 'text-blue-600' };
  return { label: 'Below Expectation', textClass: 'text-amber-600' };
};

const AppraisalPortal = ({ user, isRM }) => {
  // Common state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isRM ? 'manager-setup' : 'my-appraisals');
  
  // Fiscal year state
  const [currentFiscalYear, setCurrentFiscalYear] = useState('');
  const [fiscalYearOptions, setFiscalYearOptions] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  
  // Manager state - Cycle setup
  const [appraisalKpps, setAppraisalKpps] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedKppIds, setSelectedKppIds] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [currentCycle, setCurrentCycle] = useState(null);
  const [teamByLevel, setTeamByLevel] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  
  // Manager state - Review forms
  const [pendingForms, setPendingForms] = useState([]);
  const [teamForms, setTeamForms] = useState([]);
  const [selectedFormForReview, setSelectedFormForReview] = useState(null);
  const [appraiserScores, setAppraiserScores] = useState([]);
  const [appraiserFeedback, setAppraiserFeedback] = useState('');
  
  // HR Review state
  const [isHR, setIsHR] = useState(false);
  const [hrPendingForms, setHrPendingForms] = useState([]);
  const [selectedHrForm, setSelectedHrForm] = useState(null);
  const [showHrFormDialog, setShowHrFormDialog] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [finalScoreInput, setFinalScoreInput] = useState('');
  const [completing, setCompleting] = useState(false);
  
  // Employee state
  const [myForms, setMyForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selfScores, setSelfScores] = useState([]);
  const [appraiseeComments, setAppraiseeComments] = useState({});
  const [finalAppraiseFeedback, setFinalAppraiseFeedback] = useState('');
  
  // Promotion Recommendation state (Manager only)
  const [promotionRecommendation, setPromotionRecommendation] = useState(false);
  const [promotionJustification, setPromotionJustification] = useState('');
  
  // Dialog state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showSatisfactionDialog, setShowSatisfactionDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [formMaximized, setFormMaximized] = useState(false);
  const [reviewMaximized, setReviewMaximized] = useState(false);
  const [hrFormMaximized, setHrFormMaximized] = useState(false);

  // Live appraiser score — mirrors backend formula: sum((score / max_value) * weightage)
  const liveAppraiserScore = useMemo(() => {
    if (!selectedFormForReview) return null;
    let total = 0;
    let any = false;
    for (const kpp of selectedFormForReview.kpp_scores || []) {
      const entry = appraiserScores.find(s => s.kpp_id === kpp.kpp_id);
      const raw = entry && entry.appraiser_score !== '' && entry.appraiser_score != null
        ? parseFloat(entry.appraiser_score)
        : (kpp.appraiser_score ?? null);
      const maxVal = parseFloat(kpp.max_value) || 0;
      if (raw === null || Number.isNaN(raw) || maxVal <= 0) continue;
      total += (raw / maxVal) * (parseFloat(kpp.weightage) || 0);
      any = true;
    }
    return any ? Math.round(total * 100) / 100 : null;
  }, [appraiserScores, selectedFormForReview]);

  // Check if user is HR for any employee
  const checkIfHR = useCallback(async () => {
    const attempt = async () => {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      return axios.get(`${API}/appraisal/hr-pending`, config);
    };
    try {
      const response = await attempt();
      // If the API call succeeds, user has HR access
      setIsHR(true);
      setHrPendingForms(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // User is not HR for anyone
        setIsHR(false);
        return;
      }
      // Transient failure (5xx/network): retry once before hiding the tab
      console.error('Error checking HR status:', error);
      try {
        await new Promise((r) => setTimeout(r, 2000));
        const response = await attempt();
        setIsHR(true);
        setHrPendingForms(response.data);
      } catch (retryErr) {
        if (retryErr.response?.status === 403) setIsHR(false);
        console.error('HR status retry failed:', retryErr);
      }
    }
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchFiscalYears();
    fetchAppraisalKpps();
    fetchLevels();
    checkIfHR();
  }, [checkIfHR]);

  useEffect(() => {
    if (selectedFiscalYear) {
      if (isRM) {
        fetchCycles();
        fetchTeamForms();
        fetchPendingForms();
      }
      fetchMyForms();
    }
  }, [selectedFiscalYear, isRM]);

  useEffect(() => {
    if (selectedLevels.length > 0) {
      fetchTeamByLevel();
    } else {
      setTeamByLevel([]);
      setSelectedEmployeeIds([]);
    }
  }, [selectedLevels]);

  const fetchFiscalYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/fiscal-years`, config);
      if (response.data?.options?.length) {
        setCurrentFiscalYear(response.data.current);
        setFiscalYearOptions(response.data.options);
        setSelectedFiscalYear(response.data.current);
        return;
      }
      throw new Error('empty fiscal-year options');
    } catch (error) {
      console.error('Error fetching fiscal years:', error);
      // Fallback: compute the Sept–Aug fiscal years client-side so the
      // dropdown never renders empty on a transient API failure.
      const now = new Date();
      const start = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
      const current = `${start}-${start + 1}`;
      setCurrentFiscalYear(current);
      setFiscalYearOptions([start - 2, start - 1, start, start + 1].map((y) => `${y}-${y + 1}`));
      setSelectedFiscalYear(current);
    }
  };

  const fetchAppraisalKpps = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/kpps`, config);
      setAppraisalKpps(response.data);
    } catch (error) {
      console.error('Error fetching appraisal KPPs:', error);
    }
  };

  const fetchLevels = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/levels`, config);
      setAvailableLevels(response.data.levels);
    } catch (error) {
      console.error('Error fetching levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCycles = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/cycles?fiscal_year=${selectedFiscalYear}`, config);
      setCycles(response.data);
      const activeCycle = response.data.find(c => c.status === 'draft' && c.fiscal_year === selectedFiscalYear)
        || response.data.find(c => c.status === 'published' && c.fiscal_year === selectedFiscalYear);
      if (activeCycle) {
        setCurrentCycle(activeCycle);
        setSelectedLevels(activeCycle.levels || []);
        setSelectedKppIds(activeCycle.kpp_config?.map(k => k.kpp_id) || []);
      }
    } catch (error) {
      console.error('Error fetching cycles:', error);
    }
  };

  const fetchTeamByLevel = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/team-by-level?levels=${selectedLevels.join(',')}`, config);
      setTeamByLevel(response.data);
      setSelectedEmployeeIds((response.data || []).map(e => e.employee_id));
    } catch (error) {
      console.error('Error fetching team by level:', error);
    }
  };

  const fetchTeamForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/team-forms?fiscal_year=${selectedFiscalYear}`, config);
      setTeamForms(response.data);
    } catch (error) {
      console.error('Error fetching team forms:', error);
    }
  };

  const fetchPendingForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/pending-forms?fiscal_year=${selectedFiscalYear}`, config);
      setPendingForms(response.data);
    } catch (error) {
      console.error('Error fetching pending forms:', error);
    }
  };

  const fetchMyForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/my-forms?fiscal_year=${selectedFiscalYear}`, config);
      setMyForms(response.data);
    } catch (error) {
      console.error('Error fetching my forms:', error);
    }
  };

  const handleLevelToggle = (level) => {
    setSelectedLevels(prev => 
      prev.includes(level) 
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  const handleKppToggle = (kppId) => {
    setSelectedKppIds(prev =>
      prev.includes(kppId)
        ? prev.filter(id => id !== kppId)
        : [...prev, kppId]
    );
  };

  const handleSaveCycle = async () => {
    if (selectedLevels.length === 0) {
      toast.error('Please select at least one level');
      return;
    }
    
    if (selectedKppIds.length === 0) {
      toast.error('Please select at least one KPP');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/appraisal/cycles`, {
        fiscal_year: selectedFiscalYear,
        levels: selectedLevels,
        kpp_ids: selectedKppIds,
        employee_deadline_days: 15,
        manager_deadline_days: 15
      }, config);
      
      toast.success(response.data.message);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cycle');
    }
  };

  const handlePublishCycle = async () => {
    if (!currentCycle) {
      toast.error('Please save the cycle first');
      return;
    }
    if (teamByLevel.length > 0 && selectedEmployeeIds.length === 0) {
      toast.error('Please select at least one employee to publish to');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/appraisal/cycles/publish`, {
        cycle_id: currentCycle.id,
        employee_ids: selectedEmployeeIds
      }, config);
      
      toast.success(response.data.message);
      fetchCycles();
      fetchTeamForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish cycle');
    }
  };

  const openFormForSelfAssessment = (form) => {
    setSelectedForm(form);
    setSelfScores(form.kpp_scores.map(k => ({
      kpp_id: k.kpp_id,
      self_score: k.self_score || '',
      appraisee_comment: k.appraisee_comment || ''
    })));
    setFinalAppraiseFeedback(form.final_appraisee_feedback || '');
    setShowFormDialog(true);
  };

  const openFormForReview = (form) => {
    setSelectedFormForReview(form);
    setAppraiserScores(form.kpp_scores.map(k => ({
      kpp_id: k.kpp_id,
      appraiser_score: k.appraiser_score || '',
      appraiser_comment: k.appraiser_comment || ''
    })));
    setAppraiserFeedback(form.final_appraiser_feedback || '');
    // Initialize promotion recommendation state from form
    setPromotionRecommendation(form.promotion_recommendation || false);
    setPromotionJustification(form.promotion_justification || '');
    setShowReviewDialog(true);
  };

  const handleSaveSelfAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/appraisal/forms/save`, {
        form_id: selectedForm.id,
        kpp_scores: selfScores,
        final_appraisee_feedback: finalAppraiseFeedback
      }, config);
      
      toast.success('Self-assessment saved');
      fetchMyForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleSubmitToAppraiser = async () => {
    // Validate all self scores are filled
    for (const score of selfScores) {
      if (score.self_score === '' || score.self_score === null) {
        toast.error('Please fill all self-scores before submitting');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // First save
      await axios.post(`${API}/appraisal/forms/save`, {
        form_id: selectedForm.id,
        kpp_scores: selfScores,
        final_appraisee_feedback: finalAppraiseFeedback
      }, config);
      
      // Then submit
      await axios.post(`${API}/appraisal/forms/submit-to-appraiser`, {
        form_id: selectedForm.id
      }, config);
      
      toast.success('Submitted to manager for review');
      setShowFormDialog(false);
      fetchMyForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleSaveAppraiserScores = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/appraisal/forms/save-appraiser-scores`, {
        form_id: selectedFormForReview.id,
        kpp_scores: appraiserScores,
        final_appraiser_feedback: appraiserFeedback,
        promotion_recommendation: promotionRecommendation,
        promotion_justification: promotionRecommendation ? promotionJustification : null
      }, config);
      
      toast.success(`Scores saved. Overall Score: ${response.data.overall_score}%`);
      fetchPendingForms();
      fetchTeamForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scores');
    }
  };

  const handleSubmitAppraiserReview = async () => {
    // Validate all appraiser scores
    for (const score of appraiserScores) {
      if (score.appraiser_score === '' || score.appraiser_score === null) {
        toast.error('Please fill all appraiser scores before submitting');
        return;
      }
    }

    // Validate promotion justification if recommendation is yes
    if (promotionRecommendation && !promotionJustification.trim()) {
      toast.error('Please provide justification for promotion recommendation');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // First save
      await axios.post(`${API}/appraisal/forms/save-appraiser-scores`, {
        form_id: selectedFormForReview.id,
        kpp_scores: appraiserScores,
        final_appraiser_feedback: appraiserFeedback,
        promotion_recommendation: promotionRecommendation,
        promotion_justification: promotionRecommendation ? promotionJustification : null
      }, config);
      
      // Then submit
      await axios.post(`${API}/appraisal/forms/submit-appraiser-review`, {
        form_id: selectedFormForReview.id
      }, config);
      
      toast.success('Review submitted to employee');
      setShowReviewDialog(false);
      fetchPendingForms();
      fetchTeamForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const handleSendToHR = (satisfied) => {
    setShowSatisfactionDialog(false);
    submitToHR(satisfied);
  };

  const submitToHR = async (satisfied) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/appraisal/forms/send-to-hr`, {
        form_id: selectedForm.id,
        satisfied: satisfied
      }, config);
      
      toast.success('Appraisal sent to HR');
      setShowFormDialog(false);
      fetchMyForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to HR');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { label: 'Draft', class: 'bg-gray-500' },
      'published': { label: 'Published', class: 'bg-blue-500' },
      'pending_appraiser': { label: 'Pending Manager', class: 'bg-yellow-500' },
      'pending_appraisee_review': { label: 'Manager Reviewed', class: 'bg-purple-500' },
      'pending_hr': { label: 'Pending HR', class: 'bg-orange-500' },
      'completed': { label: 'Completed', class: 'bg-green-500' },
      'in_progress': { label: 'In Progress', class: 'bg-blue-400' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-gray-400' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const updateSelfScore = (kppId, field, value) => {
    setSelfScores(prev => prev.map(s => 
      s.kpp_id === kppId ? { ...s, [field]: value } : s
    ));
  };

  const updateAppraiserScore = (kppId, field, value) => {
    setAppraiserScores(prev => prev.map(s => 
      s.kpp_id === kppId ? { ...s, [field]: value } : s
    ));
  };

  // HR Review handlers
  const handleViewHrForm = (form) => {
    setSelectedHrForm(form);
    setShowHrFormDialog(true);
  };

  const openCompleteDialog = (form) => {
    setCompleteTarget(form);
    const prefill = form.overall_score ?? form.appraiser_score ?? form.self_score;
    setFinalScoreInput(prefill === null || prefill === undefined ? '' : String(prefill));
  };

  const confirmCompleteAppraisal = async () => {
    if (!completeTarget) return;
    const score = parseFloat(finalScoreInput);
    if (Number.isNaN(score) || score < 0 || score > 100) {
      toast.error('Final score must be a number between 0 and 100');
      return;
    }
    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/appraisal/forms/complete`, {
        form_id: completeTarget.id,
        final_score: score,
      }, config);
      toast.success(response.data.message);
      setCompleteTarget(null);
      setFinalScoreInput('');
      setShowHrFormDialog(false);
      setSelectedHrForm(null);
      checkIfHR(); // Refresh HR pending forms
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete appraisal');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appraisal-portal">
      {/* Fiscal Year Selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-green-600" />
              Appraisal Portal
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Fiscal Year:</Label>
              <Select value={selectedFiscalYear} onValueChange={setSelectedFiscalYear}>
                <SelectTrigger className="w-36" data-testid="fiscal-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fiscalYearOptions.map(fy => (
                    <SelectItem key={fy} value={fy} data-testid={`fiscal-year-option-${fy}`}>{fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <CardDescription>
            Appraisal cycle for FY {selectedFiscalYear} (September to August)
          </CardDescription>
        </CardHeader>
      </Card>

      {isRM ? (
        /* Manager View */
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full max-w-2xl ${isHR ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <TabsTrigger value="manager-setup" className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Setup
            </TabsTrigger>
            <TabsTrigger value="team-forms" className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              Team
            </TabsTrigger>
            <TabsTrigger value="pending-review" className="flex items-center gap-1 relative">
              <FileText className="w-4 h-4" />
              Review
              {pendingForms.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingForms.length}
                </span>
              )}
            </TabsTrigger>
            {isHR && (
              <TabsTrigger value="hr-review" className="flex items-center gap-1 relative" data-testid="hr-review-tab">
                <CheckCircle className="w-4 h-4" />
                HR Review
                {hrPendingForms.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {hrPendingForms.length}
                  </span>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="my-appraisals" className="flex items-center gap-1">
              <Star className="w-4 h-4" />
              My Appraisal
            </TabsTrigger>
          </TabsList>

          {/* Manager Setup Tab */}
          <TabsContent value="manager-setup" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Level Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Select Employee Levels</CardTitle>
                  <CardDescription>Choose levels to include in this appraisal cycle</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    {availableLevels.map(level => (
                      <div 
                        key={level}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedLevels.includes(level) 
                            ? 'bg-blue-50 border-blue-500' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleLevelToggle(level)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={selectedLevels.includes(level)} />
                          <span className="font-medium">Level {level}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedLevels.length > 0 && teamByLevel.length > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium">Publish to full-time employees ({selectedEmployeeIds.length}/{teamByLevel.length} selected):</p>
                        <div className="flex gap-3">
                          <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setSelectedEmployeeIds(teamByLevel.map(e => e.employee_id))} data-testid="select-all-employees-btn">Select all</button>
                          <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setSelectedEmployeeIds([])} data-testid="clear-employees-btn">Clear</button>
                        </div>
                      </div>
                      <div className="max-h-56 overflow-y-auto border rounded-lg divide-y bg-white" data-testid="eligible-employees-list">
                        {teamByLevel.map(emp => (
                          <label key={emp.employee_id} className="flex items-center gap-3 p-2.5 hover:bg-gray-50 cursor-pointer" data-testid={`eligible-employee-${emp.employee_id}`}>
                            <Checkbox
                              checked={selectedEmployeeIds.includes(emp.employee_id)}
                              onCheckedChange={(checked) => setSelectedEmployeeIds(prev => checked ? [...prev, emp.employee_id] : prev.filter(id => id !== emp.employee_id))}
                            />
                            <span className="text-sm font-medium">{emp.employee_name}</span>
                            <Badge variant="outline" className="text-xs">L{String(emp.level ?? '').replace(/^level\s*/i, '')}</Badge>
                            {emp.department && <span className="text-xs text-gray-400">{emp.department}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* KPP Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Select KPPs</CardTitle>
                  <CardDescription>
                    Select any combination of KPPs — total weightage should equal 100%
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Qualitative KPPs */}
                    <div>
                      <h4 className="font-medium text-sm text-purple-700 mb-2">Qualitative KPPs</h4>
                      {appraisalKpps.filter(k => k.kpp_type === 'qualitative').map(kpp => (
                        <div 
                          key={kpp.id}
                          className={`p-2 border rounded mb-2 cursor-pointer ${
                            selectedKppIds.includes(kpp.id) ? 'bg-purple-50 border-purple-400' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleKppToggle(kpp.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Checkbox checked={selectedKppIds.includes(kpp.id)} />
                              <span className="text-sm font-medium">{kpp.name}</span>
                            </div>
                            <Badge className="bg-purple-600">{kpp.weightage}%</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Quantitative KPPs */}
                    <div>
                      <h4 className="font-medium text-sm text-blue-700 mb-2">Quantitative KPPs</h4>
                      {appraisalKpps.filter(k => k.kpp_type === 'quantitative').map(kpp => (
                        <div 
                          key={kpp.id}
                          className={`p-2 border rounded mb-2 cursor-pointer ${
                            selectedKppIds.includes(kpp.id) ? 'bg-blue-50 border-blue-400' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleKppToggle(kpp.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Checkbox checked={selectedKppIds.includes(kpp.id)} />
                              <div>
                                <span className="text-sm font-medium">{kpp.name}</span>
                                <p className="text-xs text-gray-500">{kpp.description}</p>
                              </div>
                            </div>
                            <Badge className="bg-blue-600">{kpp.weightage}%</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Selected: {selectedKppIds.length} KPP(s)</span>
                      <span>{appraisalKpps.filter(k => selectedKppIds.includes(k.id) && k.kpp_type === 'qualitative').length} qualitative · {appraisalKpps.filter(k => selectedKppIds.includes(k.id) && k.kpp_type === 'quantitative').length} quantitative</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium">
                      <span>Total Weightage:</span>
                      <span className={
                        appraisalKpps.filter(k => selectedKppIds.includes(k.id)).reduce((sum, k) => sum + k.weightage, 0) === 100
                          ? 'text-green-600' : 'text-orange-600'
                      }>
                        {appraisalKpps.filter(k => selectedKppIds.includes(k.id)).reduce((sum, k) => sum + k.weightage, 0)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    {currentCycle && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Cycle Status:</span>
                        {getStatusBadge(currentCycle.status)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={handleSaveCycle} data-testid="save-cycle-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Save Draft
                    </Button>
                    <Button 
                      onClick={handlePublishCycle} 
                      disabled={!currentCycle || (currentCycle.status !== 'draft' && currentCycle.status !== 'published')}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="publish-cycle-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Publish & Notify
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Forms Tab */}
          <TabsContent value="team-forms">
            <Card>
              <CardHeader>
                <CardTitle>Team Appraisal Status</CardTitle>
                <CardDescription>Track appraisal progress for your team</CardDescription>
              </CardHeader>
              <CardContent>
                {teamForms.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No appraisal forms published yet.</p>
                    <p className="text-sm">Set up and publish a cycle to begin.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teamForms.map(form => (
                      <div key={form.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div>
                          <p className="font-medium">{form.employee_name}</p>
                          <p className="text-sm text-gray-500">Level {form.employee_level} • {form.employee_code}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          {form.overall_score !== null && (
                            <span className="text-lg font-bold text-blue-600">{form.overall_score}%</span>
                          )}
                          {getStatusBadge(form.status)}
                          {form.status === 'pending_appraiser' && (
                            <Button size="sm" onClick={() => openFormForReview(form)}>
                              Review
                            </Button>
                          )}
                          {form.status !== 'draft' && form.status !== 'pending_appraiser' && (
                            <Button size="sm" variant="outline" onClick={() => openFormForReview(form)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Review Tab */}
          <TabsContent value="pending-review">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  Pending Your Review
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingForms.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                    <p>No pending reviews!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingForms.map(form => (
                      <div key={form.id} className="flex items-center justify-between p-4 border rounded-lg bg-yellow-50 border-yellow-200">
                        <div>
                          <p className="font-medium">{form.employee_name}</p>
                          <p className="text-sm text-gray-500">
                            Submitted: {new Date(form.employee_submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button onClick={() => openFormForReview(form)}>
                          Start Review
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HR Review Tab */}
          {isHR && (
            <TabsContent value="hr-review">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                    HR Review - Pending Appraisals
                  </CardTitle>
                  <CardDescription>
                    Review and complete employee appraisals assigned to you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {hrPendingForms.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                      <p className="text-lg">No appraisals pending HR review</p>
                      <p className="text-sm mt-2">Appraisals will appear here once managers send them for your approval</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 text-left font-medium">Employee</th>
                            <th className="p-3 text-left font-medium">Department</th>
                            <th className="p-3 text-left font-medium">Manager</th>
                            <th className="p-3 text-center font-medium">Self Score</th>
                            <th className="p-3 text-center font-medium">Manager Score</th>
                            <th className="p-3 text-left font-medium">Sent At</th>
                            <th className="p-3 text-center font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {hrPendingForms.map(form => (
                            <tr key={form.id} className="hover:bg-gray-50">
                              <td className="p-3 font-medium">{form.employee_name}</td>
                              <td className="p-3 text-gray-600">{form.department || '-'}</td>
                              <td className="p-3 text-gray-600">{form.manager_name}</td>
                              <td className="p-3 text-center">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                  {form.self_score?.toFixed(1) || '-'}%
                                </Badge>
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant="outline" className="bg-green-50 text-green-700">
                                  {form.appraiser_score?.toFixed(1) || '-'}%
                                </Badge>
                              </td>
                              <td className="p-3 text-sm text-gray-500">
                                {form.sent_to_hr_at ? new Date(form.sent_to_hr_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex gap-2 justify-center">
                                  <Button variant="outline" size="sm" onClick={() => handleViewHrForm(form)}>
                                    View
                                  </Button>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openCompleteDialog(form)} data-testid={`hr-complete-btn-${form.id}`}>
                                    Complete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Manager's Own Appraisal Tab */}
          <TabsContent value="my-appraisals">
            {renderEmployeeAppraisalView()}
          </TabsContent>
        </Tabs>
      ) : (
        /* Employee Only View - Also show HR tab if user is HR for someone */
        isHR ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="my-appraisals" className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                My Appraisal
              </TabsTrigger>
              <TabsTrigger value="hr-review" className="flex items-center gap-1 relative" data-testid="hr-review-tab">
                <CheckCircle className="w-4 h-4" />
                HR Review
                {hrPendingForms.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {hrPendingForms.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="my-appraisals">
              {renderEmployeeAppraisalView()}
            </TabsContent>
            
            <TabsContent value="hr-review">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                    HR Review - Pending Appraisals
                  </CardTitle>
                  <CardDescription>
                    Review and complete employee appraisals assigned to you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {hrPendingForms.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                      <p className="text-lg">No appraisals pending HR review</p>
                      <p className="text-sm mt-2">Appraisals will appear here once managers send them for your approval</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 text-left font-medium">Employee</th>
                            <th className="p-3 text-left font-medium">Department</th>
                            <th className="p-3 text-left font-medium">Manager</th>
                            <th className="p-3 text-center font-medium">Self Score</th>
                            <th className="p-3 text-center font-medium">Manager Score</th>
                            <th className="p-3 text-left font-medium">Sent At</th>
                            <th className="p-3 text-center font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {hrPendingForms.map(form => (
                            <tr key={form.id} className="hover:bg-gray-50">
                              <td className="p-3 font-medium">{form.employee_name}</td>
                              <td className="p-3 text-gray-600">{form.department || '-'}</td>
                              <td className="p-3 text-gray-600">{form.manager_name}</td>
                              <td className="p-3 text-center">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                  {form.self_score?.toFixed(1) || '-'}%
                                </Badge>
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant="outline" className="bg-green-50 text-green-700">
                                  {form.appraiser_score?.toFixed(1) || '-'}%
                                </Badge>
                              </td>
                              <td className="p-3 text-sm text-gray-500">
                                {form.sent_to_hr_at ? new Date(form.sent_to_hr_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex gap-2 justify-center">
                                  <Button variant="outline" size="sm" onClick={() => handleViewHrForm(form)}>
                                    View
                                  </Button>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openCompleteDialog(form)} data-testid={`hr-complete-btn-${form.id}`}>
                                    Complete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          renderEmployeeAppraisalView()
        )
      )}

      {/* Self-Assessment Form Dialog */}
      <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
        <DialogContent className={formMaximized
          ? "max-w-none w-screen h-screen max-h-none left-0 top-0 translate-x-0 translate-y-0 rounded-none overflow-y-auto"
          : "max-w-4xl max-h-[90vh] overflow-y-auto"}>
          <button
            type="button"
            onClick={() => setFormMaximized(v => !v)}
            className="absolute right-12 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            title={formMaximized ? 'Restore size' : 'Maximize to full page'}
            data-testid="appraisal-form-maximize-btn"
          >
            {formMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <DialogHeader>
            <DialogTitle>Appraisal Form - FY {selectedForm?.fiscal_year}</DialogTitle>
            <DialogDescription>
              {selectedForm?.status === 'draft' && 'Fill your self-assessment scores and comments'}
              {selectedForm?.status === 'pending_appraisee_review' && 'Review manager feedback and submit to HR'}
              {selectedForm?.status === 'pending_hr' && 'Waiting for HR to complete'}
              {selectedForm?.status === 'completed' && 'Appraisal completed'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedForm && (
            <div className="space-y-6">
              {/* Deadline Box */}
              {selectedForm.employee_deadline && (
                <div className={`p-4 rounded-lg border-2 ${
                  new Date(selectedForm.employee_deadline) < new Date() 
                    ? 'bg-red-50 border-red-300' 
                    : new Date(selectedForm.employee_deadline) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                    ? 'bg-yellow-50 border-yellow-300'
                    : 'bg-blue-50 border-blue-300'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className={`w-5 h-5 ${
                        new Date(selectedForm.employee_deadline) < new Date() 
                          ? 'text-red-600' 
                          : 'text-blue-600'
                      }`} />
                      <span className="font-medium">Self-Assessment Deadline:</span>
                    </div>
                    <span className={`font-bold text-lg ${
                      new Date(selectedForm.employee_deadline) < new Date() 
                        ? 'text-red-600' 
                        : 'text-blue-700'
                    }`}>
                      {new Date(selectedForm.employee_deadline).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  {new Date(selectedForm.employee_deadline) < new Date() && (
                    <p className="text-red-600 text-sm mt-2 font-medium">⚠️ Deadline has passed!</p>
                  )}
                </div>
              )}

              {/* KPP Scores Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left">KPP</th>
                      <th className="p-3 text-center w-24">Type</th>
                      <th className="p-3 text-center w-20">Weight</th>
                      <th className="p-3 text-center w-24">Self Score</th>
                      <th className="p-3 text-center w-24">Manager</th>
                      <th className="p-3 text-left">Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedForm.kpp_scores.map((kpp, idx) => {
                      const selfScore = selfScores.find(s => s.kpp_id === kpp.kpp_id);
                      return (
                        <tr key={kpp.kpp_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-3">
                            <p className="font-medium">{kpp.kpp_name}</p>
                            <p className="text-xs text-gray-500">
                              {kpp.measurement_type}: {kpp.min_value} - {kpp.max_value}
                            </p>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={kpp.kpp_type === 'qualitative' ? 'bg-purple-500' : 'bg-blue-500'}>
                              {kpp.kpp_type === 'qualitative' ? 'Qual' : 'Quant'}
                            </Badge>
                          </td>
                          <td className="p-3 text-center font-medium">{kpp.weightage}%</td>
                          <td className="p-3">
                            <Input
                              type="number"
                              min={kpp.min_value}
                              max={kpp.max_value}
                              value={selfScore?.self_score || ''}
                              onChange={(e) => updateSelfScore(kpp.kpp_id, 'self_score', e.target.value)}
                              className="w-20 text-center"
                              disabled={selectedForm.status !== 'draft'}
                            />
                          </td>
                          <td className="p-3 text-center">
                            {kpp.appraiser_score !== null ? (
                              <span className="font-bold text-blue-600">{kpp.appraiser_score}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="space-y-2">
                              <Textarea
                                placeholder="Your comment"
                                value={selfScore?.appraisee_comment || ''}
                                onChange={(e) => updateSelfScore(kpp.kpp_id, 'appraisee_comment', e.target.value)}
                                className="text-sm h-16"
                                disabled={selectedForm.status !== 'draft'}
                              />
                              {kpp.appraiser_comment && (
                                <div className="p-2 bg-blue-50 rounded text-sm">
                                  <span className="font-medium text-blue-700">Manager: </span>
                                  {kpp.appraiser_comment}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Final Score — visible to the employee only after HR completes.
                  Before that, only per-KPP manager scores/comments show in the table above. */}
              {selectedForm.status === 'completed' && selectedForm.overall_score != null && (
                <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg text-center" data-testid="employee-final-score">
                  <p className="text-sm opacity-90">Final Score</p>
                  <p className="text-4xl font-bold">{selectedForm.overall_score}%</p>
                  <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 tracking-wide" data-testid="employee-score-rating">
                    {getScoreRating(selectedForm.overall_score).label}
                  </span>
                  {selectedForm.hr_final_score != null && (
                    <p className="text-xs opacity-75 mt-1">Finalized by HR</p>
                  )}
                </div>
              )}

              {/* Final Feedback */}
              <div className="space-y-4">
                <div>
                  <Label>Your Final Feedback</Label>
                  <Textarea
                    value={finalAppraiseFeedback}
                    onChange={(e) => setFinalAppraiseFeedback(e.target.value)}
                    placeholder="Share your overall thoughts about this appraisal period..."
                    className="mt-1"
                    disabled={selectedForm.status !== 'draft' && selectedForm.status !== 'pending_appraisee_review'}
                  />
                </div>
                {selectedForm.final_appraiser_feedback && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <Label className="text-blue-700">Manager's Final Feedback</Label>
                    <p className="mt-1 text-gray-700">{selectedForm.final_appraiser_feedback}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFormDialog(false)}>
              Close
            </Button>
            {selectedForm?.status === 'draft' && (
              <>
                <Button variant="outline" onClick={handleSaveSelfAssessment}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Draft
                </Button>
                <Button onClick={handleSubmitToAppraiser} className="bg-blue-600">
                  <Send className="w-4 h-4 mr-2" />
                  Send to Appraiser
                </Button>
              </>
            )}
            {selectedForm?.status === 'pending_appraisee_review' && (
              <Button onClick={() => setShowSatisfactionDialog(true)} className="bg-green-600">
                <Send className="w-4 h-4 mr-2" />
                Send to HR
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Satisfaction Confirmation Dialog */}
      <Dialog open={showSatisfactionDialog} onOpenChange={setShowSatisfactionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Submission to HR</DialogTitle>
            <DialogDescription>
              Are you satisfied with your manager's feedback?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-4 justify-center py-4">
            <Button onClick={() => handleSendToHR(true)} className="bg-green-600 hover:bg-green-700 flex-1">
              <CheckCircle className="w-4 h-4 mr-2" />
              Yes, Satisfied
            </Button>
            <Button onClick={() => handleSendToHR(false)} variant="outline" className="flex-1 border-orange-500 text-orange-600">
              <AlertCircle className="w-4 h-4 mr-2" />
              No, Not Satisfied
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manager Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className={reviewMaximized
          ? "max-w-none w-screen h-screen max-h-none left-0 top-0 translate-x-0 translate-y-0 rounded-none overflow-y-auto"
          : "max-w-4xl max-h-[90vh] overflow-y-auto"}>
          <button
            type="button"
            onClick={() => setReviewMaximized(v => !v)}
            className="absolute right-12 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            title={reviewMaximized ? 'Restore size' : 'Maximize to full page'}
            data-testid="review-form-maximize-btn"
          >
            {reviewMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <DialogHeader>
            <DialogTitle>Review: {selectedFormForReview?.employee_name}</DialogTitle>
            <DialogDescription>
              FY {selectedFormForReview?.fiscal_year} • Level {selectedFormForReview?.employee_level}
            </DialogDescription>
          </DialogHeader>
          
          {selectedFormForReview && (
            <div className="space-y-6">
              {/* Deadline Box for Manager */}
              {selectedFormForReview.manager_deadline && (
                <div className={`p-4 rounded-lg border-2 ${
                  new Date(selectedFormForReview.manager_deadline) < new Date() 
                    ? 'bg-red-50 border-red-300' 
                    : new Date(selectedFormForReview.manager_deadline) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                    ? 'bg-yellow-50 border-yellow-300'
                    : 'bg-blue-50 border-blue-300'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className={`w-5 h-5 ${
                        new Date(selectedFormForReview.manager_deadline) < new Date() 
                          ? 'text-red-600' 
                          : 'text-blue-600'
                      }`} />
                      <span className="font-medium">Review Deadline:</span>
                    </div>
                    <span className={`font-bold text-lg ${
                      new Date(selectedFormForReview.manager_deadline) < new Date() 
                        ? 'text-red-600' 
                        : 'text-blue-700'
                    }`}>
                      {new Date(selectedFormForReview.manager_deadline).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  {new Date(selectedFormForReview.manager_deadline) < new Date() && (
                    <p className="text-red-600 text-sm mt-2 font-medium">⚠️ Deadline has passed!</p>
                  )}
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left">KPP</th>
                      <th className="p-3 text-center w-20">Weight</th>
                      <th className="p-3 text-center w-24">Self Score</th>
                      <th className="p-3 text-center w-24">Your Score</th>
                      <th className="p-3 text-left">Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFormForReview.kpp_scores.map((kpp, idx) => {
                      const appScore = appraiserScores.find(s => s.kpp_id === kpp.kpp_id);
                      return (
                        <tr key={kpp.kpp_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-3">
                            <p className="font-medium">{kpp.kpp_name}</p>
                            <p className="text-xs text-gray-500">{kpp.min_value} - {kpp.max_value}</p>
                          </td>
                          <td className="p-3 text-center">{kpp.weightage}%</td>
                          <td className="p-3 text-center">
                            <span className="font-medium">{kpp.self_score || '-'}</span>
                            {kpp.appraisee_comment && (
                              <p className="text-xs text-gray-500 mt-1">{kpp.appraisee_comment}</p>
                            )}
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              min={kpp.min_value}
                              max={kpp.max_value}
                              value={appScore?.appraiser_score || ''}
                              onChange={(e) => updateAppraiserScore(kpp.kpp_id, 'appraiser_score', e.target.value)}
                              className="w-20 text-center"
                              disabled={selectedFormForReview.status !== 'pending_appraiser'}
                            />
                          </td>
                          <td className="p-3">
                            <Textarea
                              placeholder="Your comment"
                              value={appScore?.appraiser_comment || ''}
                              onChange={(e) => updateAppraiserScore(kpp.kpp_id, 'appraiser_comment', e.target.value)}
                              className="text-sm h-16"
                              disabled={selectedFormForReview.status !== 'pending_appraiser'}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Employee's Final Feedback */}
              {selectedFormForReview.final_appraisee_feedback && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="text-gray-700">Employee's Final Feedback</Label>
                  <p className="mt-1">{selectedFormForReview.final_appraisee_feedback}</p>
                </div>
              )}

              {/* Manager's Final Feedback */}
              <div>
                <Label>Your Final Feedback</Label>
                <Textarea
                  value={appraiserFeedback}
                  onChange={(e) => setAppraiserFeedback(e.target.value)}
                  placeholder="Provide overall feedback for this employee..."
                  className="mt-1"
                  disabled={selectedFormForReview.status !== 'pending_appraiser'}
                />
              </div>

              {/* Promotion Recommendation (Manager only - visible to Manager and HR) */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="promotionRecommendation"
                    checked={promotionRecommendation}
                    onChange={(e) => setPromotionRecommendation(e.target.checked)}
                    className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    disabled={selectedFormForReview.status !== 'pending_appraiser'}
                  />
                  <Label htmlFor="promotionRecommendation" className="text-purple-800 font-medium cursor-pointer">
                    Recommend for Promotion
                  </Label>
                </div>
                
                {promotionRecommendation && (
                  <div className="ml-8">
                    <Label className="text-purple-700">Justification for Promotion *</Label>
                    <Textarea
                      value={promotionJustification}
                      onChange={(e) => setPromotionJustification(e.target.value)}
                      placeholder="Please provide detailed justification for recommending this employee for promotion..."
                      className="mt-1 border-purple-200 focus:border-purple-400"
                      rows={3}
                      disabled={selectedFormForReview.status !== 'pending_appraiser'}
                    />
                  </div>
                )}
                <p className="text-xs text-purple-600 mt-2 italic">
                  Note: This recommendation is confidential and visible only to Manager and HR.
                </p>
              </div>

              {(liveAppraiserScore !== null || selectedFormForReview.overall_score !== null) && (
                <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg text-center" data-testid="review-live-score">
                  <p className="text-sm opacity-90">
                    {selectedFormForReview.status === 'pending_appraiser' ? 'Live Overall Score (updates as you score)' : 'Calculated Overall Score'}
                  </p>
                  <p className="text-4xl font-bold" data-testid="review-live-score-value">
                    {selectedFormForReview.status === 'pending_appraiser' && liveAppraiserScore !== null
                      ? liveAppraiserScore
                      : selectedFormForReview.overall_score}%
                  </p>
                  {selectedFormForReview.status === 'pending_appraiser' && (
                    <p className="text-xs opacity-75 mt-1">Unsaved preview — matches the final calculation on submit</p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Close
            </Button>
            {selectedFormForReview?.status === 'pending_appraiser' && (
              <>
                <Button variant="outline" onClick={handleSaveAppraiserScores}>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleSubmitAppraiserReview} className="bg-green-600">
                  <Send className="w-4 h-4 mr-2" />
                  Submit Review
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HR Form Details Dialog - Complete View with All Comments */}
      <Dialog open={showHrFormDialog} onOpenChange={(open) => {
        setShowHrFormDialog(open);
        if (!open) setSelectedHrForm(null);
      }}>
        <DialogContent className={hrFormMaximized
          ? "max-w-none w-screen h-screen max-h-none left-0 top-0 translate-x-0 translate-y-0 rounded-none overflow-y-auto"
          : "max-w-5xl max-h-[90vh] overflow-y-auto"}>
          <button
            type="button"
            onClick={() => setHrFormMaximized(v => !v)}
            className="absolute right-12 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            title={hrFormMaximized ? 'Restore size' : 'Maximize to full page'}
            data-testid="hr-form-maximize-btn"
          >
            {hrFormMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <DialogHeader>
            <DialogTitle>Complete Appraisal Review</DialogTitle>
            <DialogDescription>
              Review the complete appraisal form with all comments
            </DialogDescription>
          </DialogHeader>
          {selectedHrForm && (
            <div className="space-y-6">
              {/* Employee Information */}
              <div className="grid grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Employee</p>
                  <p className="font-medium">{selectedHrForm.employee_name}</p>
                  <p className="text-xs text-gray-400">{selectedHrForm.employee_code}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Manager</p>
                  <p className="font-medium">{selectedHrForm.manager_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Level</p>
                  <p className="font-medium">{selectedHrForm.employee_level || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fiscal Year</p>
                  <p className="font-medium">{selectedHrForm.fiscal_year}</p>
                </div>
              </div>

              {/* Score Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-blue-600 mb-1">Self Assessment Score</p>
                  <p className="text-2xl font-bold text-blue-700">{selectedHrForm.self_score?.toFixed(1) || '-'}%</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-green-600 mb-1">Manager Score</p>
                  <p className="text-2xl font-bold text-green-700">{selectedHrForm.appraiser_score?.toFixed(1) || '-'}%</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-purple-600 mb-1">Final Score</p>
                  <p className="text-2xl font-bold text-purple-700">{selectedHrForm.overall_score?.toFixed(1) || '-'}%</p>
                </div>
              </div>

              {/* Complete KPP Breakdown with All Comments */}
              {selectedHrForm.kpp_scores && selectedHrForm.kpp_scores.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 text-lg">KPP Scores & Comments</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left">KPP Name</th>
                          <th className="p-3 text-center w-20">Weight</th>
                          <th className="p-3 text-center w-24">Self Score</th>
                          <th className="p-3 text-center w-24">Manager Score</th>
                          <th className="p-3 text-left">Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedHrForm.kpp_scores.map((kpp, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="p-3">
                              <p className="font-medium">{kpp.kpp_name}</p>
                              <p className="text-xs text-gray-500">{kpp.min_value} - {kpp.max_value}</p>
                            </td>
                            <td className="p-3 text-center">{kpp.weightage}%</td>
                            <td className="p-3 text-center font-medium text-blue-600">{kpp.self_score || '-'}</td>
                            <td className="p-3 text-center font-medium text-green-600">{kpp.appraiser_score || '-'}</td>
                            <td className="p-3">
                              {/* Employee Comment */}
                              {kpp.appraisee_comment && (
                                <div className="mb-2 p-2 bg-blue-50 rounded text-sm">
                                  <span className="font-medium text-blue-700">Employee: </span>
                                  <span className="text-gray-700">{kpp.appraisee_comment}</span>
                                </div>
                              )}
                              {/* Manager Comment */}
                              {kpp.appraiser_comment && (
                                <div className="p-2 bg-green-50 rounded text-sm">
                                  <span className="font-medium text-green-700">Manager: </span>
                                  <span className="text-gray-700">{kpp.appraiser_comment}</span>
                                </div>
                              )}
                              {!kpp.appraisee_comment && !kpp.appraiser_comment && (
                                <span className="text-gray-400 text-sm">No comments</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Final Feedback Section */}
              <div className="grid grid-cols-2 gap-4">
                {/* Employee's Final Feedback */}
                <div>
                  <h4 className="font-semibold mb-2 text-blue-700">Employee's Final Feedback</h4>
                  <div className="bg-blue-50 p-4 rounded-lg min-h-[80px]">
                    <p className="text-gray-700">{selectedHrForm.final_appraisee_feedback || 'No feedback provided'}</p>
                  </div>
                </div>

                {/* Manager's Final Feedback */}
                <div>
                  <h4 className="font-semibold mb-2 text-green-700">Manager's Final Feedback</h4>
                  <div className="bg-green-50 p-4 rounded-lg min-h-[80px]">
                    <p className="text-gray-700">{selectedHrForm.final_appraiser_feedback || 'No feedback provided'}</p>
                  </div>
                </div>
              </div>

              {/* Promotion Recommendation (HR Only) */}
              {(selectedHrForm.promotion_recommendation !== null && selectedHrForm.promotion_recommendation !== undefined) && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h4 className="font-semibold mb-2 text-purple-700 flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    Promotion Recommendation
                  </h4>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">Recommendation:</span>
                    {selectedHrForm.promotion_recommendation ? (
                      <Badge className="bg-green-500">Yes - Recommended</Badge>
                    ) : (
                      <Badge className="bg-gray-400">No</Badge>
                    )}
                  </div>
                  {selectedHrForm.promotion_recommendation && selectedHrForm.promotion_justification && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-purple-700">Justification:</p>
                      <p className="mt-1 p-3 bg-white rounded text-gray-700">{selectedHrForm.promotion_justification}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Employee Satisfaction */}
              {selectedHrForm.satisfied_with_feedback !== null && (
                <div className={`p-4 rounded-lg ${selectedHrForm.satisfied_with_feedback ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                  <span className="font-medium">Employee Satisfaction: </span>
                  {selectedHrForm.satisfied_with_feedback ? (
                    <Badge className="bg-green-500">Satisfied with Feedback</Badge>
                  ) : (
                    <Badge className="bg-orange-500">Requested Discussion</Badge>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowHrFormDialog(false)}>
                  Close
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => openCompleteDialog(selectedHrForm)}
                  data-testid="hr-view-complete-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Complete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* HR Final Score + Complete Dialog */}
      <Dialog open={!!completeTarget} onOpenChange={(open) => !completing && !open && setCompleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="hr-complete-dialog">
          <DialogHeader>
            <DialogTitle>Complete Appraisal — {completeTarget?.employee_name}</DialogTitle>
            <DialogDescription>
              Set the final overall score. It becomes the employee's official Final Score and is included in the completion email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600">Self Score</p>
                <p className="text-xl font-bold text-blue-700">{completeTarget?.self_score != null ? completeTarget.self_score.toFixed(1) : '-'}%</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600">Manager Score</p>
                <p className="text-xl font-bold text-green-700">{completeTarget?.appraiser_score != null ? completeTarget.appraiser_score.toFixed(1) : '-'}%</p>
              </div>
            </div>
            <div>
              <Label htmlFor="hr-final-score">Final Overall Score (%) *</Label>
              <Input
                id="hr-final-score"
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={finalScoreInput}
                onChange={(e) => setFinalScoreInput(e.target.value)}
                className="mt-1 text-center text-lg font-bold"
                autoFocus
                data-testid="hr-final-score-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteTarget(null)} disabled={completing} data-testid="hr-complete-cancel-btn">
              Cancel
            </Button>
            <Button onClick={confirmCompleteAppraisal} disabled={completing || finalScoreInput === ''} className="bg-green-600 hover:bg-green-700" data-testid="hr-complete-confirm-btn">
              {completing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {completing ? 'Completing…' : 'Complete Appraisal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Helper function for employee view (used in both employee-only and manager's own appraisal)
  function renderEmployeeAppraisalView() {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            My Appraisals
          </CardTitle>
          <CardDescription>View and fill your appraisal forms</CardDescription>
        </CardHeader>
        <CardContent>
          {myForms.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No appraisal forms available for FY {selectedFiscalYear}.</p>
              <p className="text-sm">Your manager will publish the appraisal cycle when ready.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {myForms.map(form => (
                <div 
                  key={form.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">FY {form.fiscal_year}</p>
                      {getStatusBadge(form.status)}
                    </div>
                    <p className="text-sm text-gray-500">Manager: {form.manager_name}</p>
                    {form.employee_deadline && form.status === 'draft' && (
                      <p className="text-xs text-orange-600">
                        Deadline: {new Date(form.employee_deadline).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Final score only after HR completes; manager overall stays hidden from the employee */}
                    {form.status === 'completed' && form.overall_score != null && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{form.overall_score}%</p>
                        <p className="text-xs text-gray-500">Final Score</p>
                        <p className={`text-xs font-semibold ${getScoreRating(form.overall_score).textClass}`} data-testid={`employee-score-rating-${form.id}`}>{getScoreRating(form.overall_score).label}</p>
                      </div>
                    )}
                    <Button onClick={() => openFormForSelfAssessment(form)}>
                      {form.status === 'draft' ? 'Fill Form' : 
                       form.status === 'pending_appraisee_review' ? 'Review & Submit' : 'View'}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
};

export default AppraisalPortal;
